import dayjs from 'dayjs';
import { devtools, persist } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { AgentSource } from '../../api/events-client/events-client.type';
import { AgenticFinancialsChartCategory } from '../../containers/agentic-process/agentic-financials/agentic-financials.type';
import { AgenticViewType } from '../../containers/agentic-process/agentic-process.type';
import { formatToISOString } from '../../helpers/date-time-helpers/formatting/formatting';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { ChartType } from '../financials-store/financials-store.type';
import { isLoggingEnabled, logger } from '../logger/logger';
import type { AgenticProcessStore } from './agentic-process-store.type';

const initialAgenticProcessState: Pick<
  AgenticProcessStore,
  | 'selectedTab'
  | 'startDate'
  | 'endDate'
  | 'selectedAgentSource'
  | 'selectedChartCategory'
  | 'selectedChartOptions'
  | 'selectedChartType'
  | 'viewBy'
> = {
  selectedTab: AgenticViewType.Tasks,
  startDate: formatToISOString(dayjs().subtract(1, 'month').startOf('month')),
  endDate: formatToISOString(dayjs()),
  selectedAgentSource: 'all',
  selectedChartCategory: AgenticFinancialsChartCategory.Tasks,
  selectedChartOptions: [],
  selectedChartType: ChartType.Line,
  viewBy: 'cost',
};

const useAgenticProcessStoreUpdate = create<AgenticProcessStore>()(
  devtools(
    persist(
      logger((set) => ({
        ...initialAgenticProcessState,
        setSelectedTab: (selectedTab: AgenticViewType) => set({ selectedTab }),
        setStartDate: (startDate: string | null) => set({ startDate }),
        setEndDate: (endDate: string | null) => set({ endDate }),
        setDateRange: (startDate: string | null, endDate: string | null) => set({ startDate, endDate }),

        // Financial chart filters
        setSelectedAgentSource: (selectedAgentSource: AgentSource | 'all') => set({ selectedAgentSource }),
        setSelectedChartCategory: (selectedChartCategory: AgenticFinancialsChartCategory) =>
          set({ selectedChartCategory, selectedChartOptions: [] }),
        setSelectedChartOptions: (selectedChartOptions: string[]) => set({ selectedChartOptions }),
        setSelectedChartType: (selectedChartType: ChartType) => set({ selectedChartType }),
        setViewBy: (viewBy: 'cost' | 'tokens') => set({ viewBy }),
        resetFinancialFilters: () =>
          set({
            selectedAgentSource: 'all',
            selectedChartCategory: AgenticFinancialsChartCategory.Tasks,
            selectedChartOptions: [],
            selectedChartType: ChartType.Line,
            viewBy: 'cost',
          }),
      })),
      {
        name: 'agentic-process-storage',
        storage: createSafeJSONStorage(() => localStorage),
        partialize: (state) => ({
          selectedTab: state.selectedTab,
          startDate: state.startDate,
          endDate: state.endDate,
          selectedAgentSource: state.selectedAgentSource,
          selectedChartCategory: state.selectedChartCategory,
          selectedChartOptions: state.selectedChartOptions,
          selectedChartType: state.selectedChartType,
          viewBy: state.viewBy,
        }),
      },
    ),
    {
      name: 'Bloomfilter - Agentic Process Store',
      enabled: isLoggingEnabled,
    },
  ),
);

export { useAgenticProcessStoreUpdate };
